import { SurfaceProvider, Text } from "@/components/ui"
import { cn } from "@/lib/cn"
import { useReducedMotion } from "motion/react"
import { type PointerEvent, type ReactNode, useEffect, useRef, useState } from "react"
import { PrizeArt } from "./PrizeArt"
import {
  AUTO_SCRATCH_MS,
  autoScratchPath,
  COIN_CURSOR,
  COIN_IMAGE,
  dropFlakes,
  paintFoil,
  pointAlong,
  SCRATCH_COLS,
  SCRATCH_REVEAL_SHARE,
  SCRATCH_ROWS,
  scrapedCells,
  scrapeFoil,
} from "./scratch-foil"
import { storeInitials } from "./prize-wheel-geometry"

type Point = { x: number; y: number }

interface Session {
  scratched: Set<number>
  touched: boolean
  cleared: boolean
  uncovered: boolean
}

interface Latest {
  onUncovered: () => void
  reduceMotion: boolean
}

const COIN_SIZE = 28

/** Props of {@link ScratchTicket}. */
export interface ScratchTicketProps {
  logoUrl?: string | null
  storeName: string
  playable: boolean
  revealed: boolean
  autoScratch?: boolean
  onScratchStart: () => void
  onLockedTouch?: () => void
  onUncovered: () => void
  children: ReactNode
}

/** What lies under the foil once drawn: the prize art (from `artKey`) and name on a win, a gentle "Não foi dessa vez" with soft mystery art on a miss. */
export function ScratchPrize({ won, label, artKey }: { won: boolean; label: string; artKey?: string | null }) {
  if (!won)
    return (
      <span className="flex flex-col items-center gap-1">
        <PrizeArt soft label={label} size={40} />
        <Text as="span" size="xl" family="game-display" tone="muted">
          Não foi dessa vez
        </Text>
      </span>
    )
  return (
    <span className="flex flex-col items-center gap-1">
      <PrizeArt artKey={artKey} label={label} size={64} />
      <Text as="span" size="game-eyebrow" weight="bold" uppercase tracking="ticket" tone="game-accent">
        Você ganhou
      </Text>
      <Text as="span" size="2xl" leading="tight" family="game-display" tone="default">
        {label}
      </Text>
    </span>
  )
}

function TicketLogo({ logoUrl, storeName }: { logoUrl?: string | null; storeName: string }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const showLogo = Boolean(logoUrl) && failedUrl !== logoUrl
  return (
    <span className="grid h-7 w-7 shrink-0 rounded-full bg-game-metal p-game-hairline">
      <span className={cn("grid h-full w-full place-items-center overflow-hidden rounded-full", showLogo ? "bg-game-ivory" : "bg-game-metal")}>
        {logoUrl && showLogo ? (
          <img src={logoUrl} alt="" className="h-full w-full object-cover" decoding="async" onError={() => setFailedUrl(logoUrl)} />
        ) : (
          <Text as="span" size="game-micro" family="game-display" tracking="normal" tone="default">
            {storeInitials(storeName)}
          </Text>
        )}
      </span>
    </span>
  )
}

function displayFontOf(element: Element): string {
  const style = getComputedStyle(element)
  return style.getPropertyValue("--font-game-display").trim() || style.fontFamily || "sans-serif"
}

function reveal(session: Session, latest: Latest) {
  if (session.uncovered) return
  session.uncovered = true
  latest.onUncovered()
}

function scrape(canvas: HTMLCanvasElement, from: Point, to: Point, session: Session, latest: Latest, flakes: HTMLElement | null) {
  const ctx = canvas.getContext("2d")
  if (!ctx) return
  session.touched = true
  scrapeFoil(ctx, from, to)
  const { width, height } = canvas.getBoundingClientRect()
  for (const cell of scrapedCells(from, to, width, height)) session.scratched.add(cell)
  if (!latest.reduceMotion && flakes) dropFlakes(flakes, to)
  if (session.scratched.size >= SCRATCH_COLS * SCRATCH_ROWS * SCRATCH_REVEAL_SHARE) session.cleared = true
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
}

/** A lottery scratch ticket in the fixed game palette: ivory card with a gold patterned border, "RASPADINHA" header with the small store logo, silver foil with an embossed "RASPE AQUI" and a coin, a perforated stub. The coin scratches and flakes fall; half the foil gone and the coin lifted, or the end of the `autoScratch` zigzag, reveals what lies under it with a 400 ms fade; the foil is painted once and never repainted after the first touch. The foil only hides what was drawn elsewhere. */
export function ScratchTicket({
  logoUrl,
  storeName,
  playable,
  revealed,
  autoScratch = false,
  onScratchStart,
  onLockedTouch,
  onUncovered,
  children,
}: ScratchTicketProps) {
  const reduceMotion = useReducedMotion() ?? false
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const flakesRef = useRef<HTMLDivElement>(null)
  const coinRef = useRef<HTMLSpanElement>(null)
  const scratching = useRef<Point | null>(null)
  const session = useRef<Session>({ scratched: new Set(), touched: false, cleared: false, uncovered: false })
  const latest = useRef<Latest>({ onUncovered, reduceMotion })
  const [painted, setPainted] = useState(false)

  useEffect(() => {
    latest.current = { onUncovered, reduceMotion }
  })

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx) return
    const { width, height } = canvas.getBoundingClientRect()
    if (width <= 0 || height <= 0) return
    const ratio = window.devicePixelRatio || 1
    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    const font = displayFontOf(canvas)
    let cancelled = false
    paintFoil(ctx, width, height, font)
    setPainted(true)
    if ("fonts" in document)
      document.fonts.load(`700 24px ${font}`).then(
        () => {
          if (!cancelled && !session.current.touched) paintFoil(ctx, width, height, font)
        },
        () => undefined,
      )
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!autoScratch || session.current.uncovered) return
    const canvas = canvasRef.current
    const coin = coinRef.current
    if (!canvas || !canvas.getContext("2d") || latest.current.reduceMotion || typeof requestAnimationFrame !== "function") {
      reveal(session.current, latest.current)
      return
    }
    const { width, height } = canvas.getBoundingClientRect()
    const path = autoScratchPath(width, height)
    let last = path[0] ?? { x: 0, y: 0 }
    let frame = 0
    const startedAt = performance.now()
    const step = (now: number) => {
      const t = Math.min(1, (now - startedAt) / AUTO_SCRATCH_MS)
      const at = pointAlong(path, easeInOut(t))
      scrape(canvas, last, at, session.current, latest.current, flakesRef.current)
      last = at
      if (coin) {
        coin.style.opacity = t < 1 ? "1" : "0"
        coin.style.transform = `translate(${at.x - COIN_SIZE / 2}px, ${at.y - COIN_SIZE / 2}px)`
      }
      if (t < 1) frame = requestAnimationFrame(step)
      else reveal(session.current, latest.current)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [autoScratch])

  function pointOf(event: PointerEvent<HTMLCanvasElement>): Point {
    const rect = event.currentTarget.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  function start(event: PointerEvent<HTMLCanvasElement>) {
    if (!playable) {
      onLockedTouch?.()
      return
    }
    event.currentTarget.setPointerCapture?.(event.pointerId)
    const at = pointOf(event)
    scratching.current = at
    onScratchStart()
    scrape(event.currentTarget, at, at, session.current, latest.current, flakesRef.current)
  }

  function move(event: PointerEvent<HTMLCanvasElement>) {
    const from = scratching.current
    if (!from || !playable) return
    const to = pointOf(event)
    scratching.current = to
    scrape(event.currentTarget, from, to, session.current, latest.current, flakesRef.current)
  }

  function stop() {
    scratching.current = null
    if (session.current.cleared) reveal(session.current, latest.current)
  }

  return (
    <SurfaceProvider surface="game">
      <div className="mx-auto w-full max-w-game-ticket select-none font-game-body">
        <div className="relative rounded-game-ticket bg-game-ticket-border p-game-edge shadow-game-ticket">
          <div className="relative rounded-game-ticket-inner bg-game-ivory shadow-game-ticket-edge">
            <div className="flex items-center gap-2 px-3.5 pb-2 pt-3">
              <TicketLogo logoUrl={logoUrl} storeName={storeName} />
              <Text as="span" size="game-title" leading="none" tracking="ticket" align="center" family="game-display" tone="default" className="flex-1">
                RASPADINHA
              </Text>
              <Text as="span" aria-hidden="true" size="sm" leading="none" align="right" tone="game-accent" className="w-7">
                ✦
              </Text>
            </div>
            <div aria-hidden="true" className="mx-3.5 mb-2.5 h-px bg-game-rule" />
            <div className="relative mx-3.5 aspect-ticket overflow-hidden rounded-game-window shadow-game-window">
              <div aria-hidden={!revealed} className="absolute inset-0 grid place-items-center overflow-hidden bg-game-window p-4 text-center">
                {children}
              </div>
              {!painted ? (
                <div aria-hidden="true" className={cn("absolute inset-0 bg-game-foil-face", revealed && "pointer-events-none opacity-0")} />
              ) : null}
              <canvas
                ref={canvasRef}
                aria-hidden="true"
                onPointerDown={start}
                onPointerMove={move}
                onPointerUp={stop}
                onPointerCancel={stop}
                onPointerLeave={stop}
                className={cn(
                  "absolute inset-0 h-full w-full transition-opacity duration-[400ms] motion-reduce:transition-none",
                  playable && "touch-none",
                  revealed && "pointer-events-none opacity-0",
                )}
                style={{ cursor: playable ? COIN_CURSOR : onLockedTouch ? "pointer" : "default" }}
              />
              <div ref={flakesRef} aria-hidden="true" className="pointer-events-none absolute inset-0" />
              <span
                ref={coinRef}
                aria-hidden="true"
                className="pointer-events-none absolute left-0 top-0 size-game-coin opacity-0"
                style={{ backgroundImage: COIN_IMAGE }}
              />
            </div>
            <div className="relative mt-3">
              <div aria-hidden="true" className="mx-4 border-t-2 border-dashed border-game-gold-deep/55" />
              <span
                aria-hidden="true"
                className="absolute -left-game-notch-inset top-0 h-game-notch w-game-notch -translate-y-1/2 rounded-full bg-game-paper shadow-game-notch-left"
              />
              <span
                aria-hidden="true"
                className="absolute -right-game-notch-inset top-0 h-game-notch w-game-notch -translate-y-1/2 rounded-full bg-game-paper shadow-game-notch-right"
              />
            </div>
            <div className="flex items-center justify-between px-4 pb-3 pt-2.5">
              <Text as="span" size="game-micro" weight="bold" uppercase tracking="ticket-stub" tone="muted">
                Prêmio na hora
              </Text>
              <Text as="span" aria-hidden="true" size="game-micro" weight="bold" uppercase tracking="ticket-stub" tone="game-accent">
                ✦ ✦ ✦
              </Text>
            </div>
          </div>
        </div>
      </div>
    </SurfaceProvider>
  )
}
